import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import Popup from "../../../components/Popup";
import TransactionDetailsModal from "../../../components/TransactionDetailsModal";
import "../../css/List.css";
import "../../css/TransactionsHistory.css";
import CashOutRequestController from "../../../controller/Transaction/cashOutRequestController";
import WalletController from "../../../controller/walletController";
import { parseLocalDateFromYmd } from "../../../utils/dateRangeApiFormat";
import {
  buildCashRequestMethodSearchText,
  queryMatchesHaystack,
} from "../../../utils/cashRequestSearch";
import { mapCashRequestToTransactionDetails } from "../../../utils/cashRequestDetails";
import { TransactionType } from "../../../utils/enums";
import ProfileInfoController from "../../../controller/profileInfoController";
import {
  MFG_FCM_NAV_TARGET_KEY,
  MFG_FCM_NAV_TX_KEY,
} from "../../../services/fcmNotificationHandler";

const CashOutRequest = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage] = useState(10);
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    total: 0,
    lastPage: 0,
  });
  const [cancelingId, setCancelingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ dateFrom: null, dateTo: null, presetId: null });

  // Load basic user data for Sidebar (same pattern as other pages)
  useEffect(() => {
    try {
      const storedUser =
        sessionStorage.getItem("userData") ||
        localStorage.getItem("userData");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (_) {
      setUser(null);
    }
  }, []);

  const loadRequests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const tokenSearch = searchTerm.trim();
      const result = tokenSearch
        ? await CashOutRequestController.getCashOutByRequestToken(tokenSearch)
        : await CashOutRequestController.getAllPendingCashOut({
            page,
            perPage,
            dateFrom: dateRange.dateFrom,
            dateTo: dateRange.dateTo,
          });

      setRequests(result.items || []);
      setPagination(result.pagination || pagination);
    } catch (e) {
      setError(e.message || "Failed to load cash-out requests");
      setRequests([]);
      // eslint-disable-next-line no-console
      console.error("[CashOutRequest] Failed to load cash-out requests", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const applyFilters = () => {
    setPage(0);
    loadRequests();
  };

  const canGoPrev = page > 0;
  const canGoNext =
    pagination.lastPage != null
      ? page < Number(pagination.lastPage)
      : requests.length === perPage;

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;

      const datePart = d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const timePart = d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `${datePart} - ${timePart}`;
    } catch (_) {
      return value;
    }
  };

  const toDateInputValue = (v) => {
    if (!v) return "";
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = String(d.getFullYear());
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseMethodLabel = (value) => {
    if (!value) return "-";
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed.startsWith("{")) return value;
    try {
      const obj = JSON.parse(trimmed);
      if (obj && typeof obj === "object") {
        if (obj.en) return obj.en;
        const keys = Object.keys(obj);
        if (keys.length > 0) {
          return obj[keys[0]];
        }
      }
    } catch (_) {
      // ignore JSON parse errors and fall back to raw value
    }
    return value;
  };

  const resolveDate = (req) => {
    if (!req) return "-";
    const raw = req.raw || {};
    const value =
      req.date ||
      raw.UPDATED_AT ||
      raw.REQUEST_DATE ||
      raw.CREATED_AT ||
      raw.DATE ||
      raw.date ||
      null;
    return formatDateTime(value);
  };

  const resolveMethod = (req) => {
    if (!req) return "-";
    if (req.methodLabel) return req.methodLabel;
    const raw = req.raw || {};

    const direct =
      raw.METHOD_LABEL ||
      raw.METHOD_NAME ||
      raw.METHOD ||
      raw.method ||
      raw.PAYMENT_METHOD_LABEL ||
      raw.PAYMENT_METHOD_NAME ||
      null;

    if (direct) return parseMethodLabel(direct);

    const pm =
      raw.payment_methods ||
      raw.PAYMENT_METHOD ||
      raw.PaymentMethod ||
      null;
    if (pm && typeof pm === "object") {
      const methodNameRaw =
        pm.METHOD_LABEL ||
        pm.METHOD_NAME ||
        pm.methodLabel ||
        pm.methodName ||
        null;
      if (methodNameRaw) {
        return parseMethodLabel(methodNameRaw);
      }
    }
    return "-";
  };

  const resolveWallet = (req) => {
    if (!req) return "-";
    if (req.currencyCode) return req.currencyCode;

    const raw = req.raw || {};

    let code =
      raw.CURRENCY_CODE ||
      raw.currencyCode ||
      (raw.CURRENCY && (raw.CURRENCY.CURRENCY_CODE || raw.CURRENCY.currencyCode || raw.CURRENCY.CODE || raw.CURRENCY.code)) ||
      (raw.currencies &&
        (raw.currencies.CURRENCY_CODE || raw.currencies.currencyCode || raw.currencies.CODE || raw.currencies.code)) ||
      null;

    if (!code) {
      const requester =
        raw.requester_wallet ||
        raw.REQUESTER_WALLET ||
        raw.wallet ||
        null;

      if (requester && typeof requester === "object") {
        const cur =
          requester.CURRENCY ||
          requester.currency ||
          requester.currencies ||
          null;
        if (cur && typeof cur === "object") {
          code =
            cur.CURRENCY_CODE ||
            cur.currencyCode ||
            cur.CODE ||
            cur.code ||
            null;
        }
      }
    }

    return code || "-";
  };

  const formatAmount = (value, currencySymbol) => {
    if (value == null) return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    const formatted = num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currencySymbol ? `${formatted} ${currencySymbol}` : formatted;
  };

  const resolveCurrencySymbol = (req) => {
    if (!req) return "";
    if (req.currencySymbol) return req.currencySymbol;
    const raw = req.raw || {};
    const requester =
      raw.requester_wallet ||
      raw.REQUESTER_WALLET ||
      raw.wallet ||
      null;
    const requesterCurrency =
      requester && typeof requester === "object"
        ? requester.CURRENCY || requester.currency || requester.currencies || null
        : null;
    return (
      raw.CURRENCY_SYMBOL ||
      raw.currencySymbol ||
      (raw.CURRENCY && (raw.CURRENCY.CURRENCY_SYMBOL || raw.CURRENCY.SYMBOL || raw.CURRENCY.symbol)) ||
      (raw.currencies &&
        (raw.currencies.CURRENCY_SYMBOL || raw.currencies.SYMBOL || raw.currencies.symbol)) ||
      (requesterCurrency &&
        (requesterCurrency.CURRENCY_SYMBOL || requesterCurrency.SYMBOL || requesterCurrency.symbol)) ||
      ""
    );
  };

  const filteredRequests = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((req) => {
      const methodSearch = buildCashRequestMethodSearchText(req);
      const haystack = [
        req.requestToken,
        req.id,
        methodSearch,
        resolveMethod(req),
        resolveWallet(req),
        req.walletLabel,
        req.statusLabel,
        req.amount,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return queryMatchesHaystack(q, haystack);
    });
  }, [requests, searchTerm]);

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") applyFilters();
  };

  const handleCancelRequest = async (req) => {
    const requestToken = req?.requestToken || req?.id || null;
    if (!requestToken) return;
    setCancelingId(requestToken);
    setError(null);
    try {
      await CashOutRequestController.cancelCashOut(requestToken);
      await loadRequests();
    } catch (e) {
      // Some backend failures can occur after the delete side effect succeeds.
      // Refresh and suppress the error if this request no longer exists.
      try {
        const refreshed = await CashOutRequestController.getAllPendingCashOut({
          page,
          perPage,
        });
        const refreshedItems = refreshed?.items || [];
        setRequests(refreshedItems);
        setPagination(refreshed?.pagination || pagination);

        const stillExists = refreshedItems.some((item) => {
          const itemToken = item?.requestToken || item?.id || null;
          return itemToken === requestToken;
        });
        if (stillExists) {
          setError(e.message || "Failed to cancel cash-out request");
        }
      } catch (_) {
        setError(e.message || "Failed to cancel cash-out request");
      }
      // eslint-disable-next-line no-console
      console.error("[CashOutRequest] Failed to cancel cash-out request", e);
    } finally {
      setCancelingId(null);
      // Refresh wallet balances everywhere after a cancel attempt
      try {
        await WalletController.refreshWalletsCache();
      } catch (_) {
        // Ignore wallet refresh errors; UI will fall back to next normal refresh.
      }
    }
  };

  const openDetails = (req) => {
    if (!req) return;
    setSelectedRequest(req);
    setDetailsError(null);
    setIsDetailsOpen(true);
    // currently we reuse data already loaded for the row
    setDetailsLoading(false);
  };

  const closeDetails = () => {
    setIsDetailsOpen(false);
    setSelectedRequest(null);
    setDetailsError(null);
    setDetailsLoading(false);
  };

  const { detailsTx, detailsData } = useMemo(
    () =>
      mapCashRequestToTransactionDetails(selectedRequest, {
        typeCode: TransactionType.CASHOUT,
        typeClass: "type-cashout",
        typeLabel: t("cashout", { ns: "mobile" }),
        statusLabel: t("pending", { ns: "mobile" }),
        currentUserName: ProfileInfoController.getDisplayName(user || {}),
      }),
    [selectedRequest, t, user]
  );

  useEffect(() => {
    if (isLoading) return;
    try {
      const target = sessionStorage.getItem(MFG_FCM_NAV_TARGET_KEY);
      const txId = sessionStorage.getItem(MFG_FCM_NAV_TX_KEY);
      if (target !== "cashout_requests" || !txId) return;
      sessionStorage.removeItem(MFG_FCM_NAV_TARGET_KEY);
      sessionStorage.removeItem(MFG_FCM_NAV_TX_KEY);
      const found = requests.find(
        (r) => String(r.requestToken || r.id || "") === String(txId)
      );
      openDetails(found || { requestToken: txId, id: txId });
    } catch (_) {}
  }, [requests, isLoading]);

  return (
    <div className="accounts-list-page transactions-centered-page transactions-history-page">
      <Sidebar user={user} />
      <div className="accounts-list-content">
        <div className="transactions-history-filters">
          <div className="transactions-filter-bar w-100" aria-label="Filter cash-out requests">
            <div className="transactions-filter-row">
              <div className="transactions-filter-field transactions-filter-field--search">
                <label>{t("search", { ns: "mobile" })}</label>
                <div className="transactions-filter-input-wrap">
                  <i className="fa-solid fa-magnifying-glass" aria-hidden />
                  <input
                    type="text"
                    className="transactions-filter-input"
                    placeholder={t("search_cash_request_placeholder", { ns: "mobile" })}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyPress}
                  />
                </div>
              </div>
              <div className="transactions-filter-field">
                <label>From Date</label>
                <input
                  type="date"
                  className="transactions-filter-input"
                  value={toDateInputValue(dateRange.dateFrom)}
                  onChange={(e) => setDateRange((p) => ({ ...p, dateFrom: e.target.value ? new Date(e.target.value) : null }))}
                />
              </div>
              <div className="transactions-filter-field">
                <label>To Date</label>
                <input
                  type="date"
                  className="transactions-filter-input"
                  value={toDateInputValue(dateRange.dateTo)}
                  onChange={(e) => setDateRange((p) => ({ ...p, dateTo: parseLocalDateFromYmd(e.target.value) }))}
                />
              </div>
              <div className="transactions-filter-actions">
                <button type="button" className="transactions-filter-apply" onClick={applyFilters}>
                  Filter
                </button>
                <button
                  type="button"
                  className="transactions-filter-reset"
                  onClick={() => {
                    setSearchTerm("");
                    setDateRange({ dateFrom: null, dateTo: null, presetId: null });
                    setPage(0);
                    setTimeout(() => loadRequests(), 0);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="accounts-table-container container-fluid px-0 d-flex flex-column">
          <div className="table-top-header">
            <div className="accounts-list-title-wrapper">
              <h1 className="accounts-list-title">{t("cash_out_requests", { ns: "mobile" })}</h1>
            </div>
          </div>
          <div className="table-responsive accounts-table-scroll">
          <div className="accounts-table transactions-table cash-request-table cash-request-table--no-wallet">
            <div className="table-header-row">
              <div className="table-header-cell">{t("request_date", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("payment_method", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("amount", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("action", { ns: "mobile" })}</div>
            </div>

            {isLoading && (
              <div className="table-row no-data-row">
                <div className="table-cell no-data">{t("loading", { ns: "mobile" })}</div>
              </div>
            )}

            {!isLoading && error && (
              <div className="table-row no-data-row">
                <div className="table-cell no-data">{error}</div>
              </div>
            )}

            {!isLoading && !error && filteredRequests.length === 0 && (
              <div className="table-row no-data-row">
                <div className="table-cell no-data">{t("no_content", { ns: "mobile" })}</div>
              </div>
            )}

            {!isLoading &&
              !error &&
              filteredRequests.map((req) => (
                <div
                  className="table-row"
                  key={req.id || `${req.date}-${Math.random()}`}
                >
                  <div className="table-cell">{resolveDate(req)}</div>
                  <div className="table-cell">{resolveMethod(req)}</div>
                  <div className="table-cell">
                    {formatAmount(req.amount, resolveCurrencySymbol(req))}
                  </div>
                  <div className="table-cell">
                    <div className="request-action-buttons">
                      <button
                        type="button"
                        className="request-action-btn request-action-btn--view"
                        onClick={() => openDetails(req)}
                        title="View request"
                        aria-label="View details"
                      >
                        <i className="fa-solid fa-eye" />
                      </button>
                      <button
                        type="button"
                        className="request-action-btn request-action-btn--delete"
                        onClick={() => {
                          setSelectedRequest(req);
                          setShowDeletePopup(true);
                        }}
                        aria-label="Cancel request"
                        title="Cancel request"
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          </div>

          <div className="accounts-pagination accounts-pagination--opposite-dark">
            <div className="pagination-controls transactions-pagination-controls">
              <button
                className="pagination-btn"
                disabled={!canGoPrev}
                onClick={() =>
                  canGoPrev && setPage((prev) => Math.max(prev - 1, 0))
                }
                type="button"
              >
                <i className={`fa-solid ${isRtl ? "fa-chevron-right" : "fa-chevron-left"}`} />
              </button>
              <button className="pagination-btn active" type="button">
                <span>{page + 1}</span>
              </button>
              <button
                className="pagination-btn"
                disabled={!canGoNext}
                onClick={() => canGoNext && setPage((prev) => prev + 1)}
                type="button"
              >
                <i className={`fa-solid ${isRtl ? "fa-chevron-left" : "fa-chevron-right"}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <TransactionDetailsModal
        isOpen={isDetailsOpen && !!selectedRequest}
        onClose={closeDetails}
        detailsTx={detailsTx}
        detailsData={detailsData}
        detailsLoading={detailsLoading}
        detailsError={detailsError}
        actions={
          selectedRequest ? (
            <button
              type="button"
              className="transactions-cancel-request-btn"
              disabled={!!cancelingId}
              onClick={() => setShowDeletePopup(true)}
            >
              <i className="fa-solid fa-trash" />
              <span>{t("cancel_request", { ns: "mobile" })}</span>
            </button>
          ) : null
        }
      />
      <Popup
        isOpen={showDeletePopup && !!selectedRequest}
        onClose={() => {
          setShowDeletePopup(false);
        }}
        onConfirm={async () => {
          if (!selectedRequest) {
            setShowDeletePopup(false);
            return;
          }
          try {
            await handleCancelRequest(selectedRequest);
            closeDetails();
          } catch (e) {
            setDetailsError(
              e?.message || "Failed to cancel cash-out request"
            );
          } finally {
            setShowDeletePopup(false);
          }
        }}
        onCancel={() => setShowDeletePopup(false)}
        showCancel
        title={t("cancel_cash_out_request", { ns: "mobile" })}
        message={t("cancel_cash_out_request_message", { ns: "mobile" })}
        instruction={t("cancel_instruction", { ns: "mobile" })}
        buttonText={t("confirm", { ns: "mobile" })}
        cancelButtonText={t("cancel", { ns: "mobile" })}
        icon="fa-trash"
      />
    </div>
  );
};

export default CashOutRequest;

